import React, { useState } from "react";
import { MapPin, Calendar, Users, Scale, Heart, X, Plus, MessageSquare, PenTool } from "lucide-react";

// Added necessary imports based on your reference
import Navbar from "../Navbar/Navbar";
import RouteMap from './RouteMap';

// ─────────────────────────────────────────────────────────────────────────────
// MOCK DATA (Added startCoords and endCoords for the map)
// ─────────────────────────────────────────────────────────────────────────────
const LOAD_BOARD = [
  { id: "PL-2041", type: "Wind Turbine Blade", dims: "160'×18'×16'", weight: "48,000 lbs", from: "Austin, TX", to: "Bakersfield, CA", date: "06/11/2026", escorts: 2, pay: "$1,850", status: "Open", company: "GreenBridge Energy", startCoords: [30.2672, -97.7431], endCoords: [35.3733, -119.0187] },
  { id: "PL-2042", type: "Bridge Beam", dims: "120'×14'×12'", weight: "62,000 lbs", from: "Dallas, TX", to: "Houston, TX", date: "06/13/2026", escorts: 1, pay: "$920", status: "Open", company: "TXHeavy Co.", startCoords: [32.7767, -96.7970], endCoords: [29.7604, -95.3698] },
  { id: "PL-2043", type: "Transformer Unit", dims: "80'×16'×15'", weight: "110,000 lbs", from: "Phoenix, AZ", to: "Las Vegas, NV", date: "06/14/2026", escorts: 3, pay: "$2,400", status: "Urgent", company: "SunGrid Power", startCoords: [33.4484, -112.0740], endCoords: [36.1699, -115.1398] },
  { id: "PL-2044", type: "Heavy Equipment", dims: "60'×14'×13'", weight: "38,000 lbs", from: "Denver, CO", to: "Salt Lake City, UT", date: "06/15/2026", escorts: 1, pay: "$750", status: "Open", company: "MountainMove LLC", startCoords: [39.7392, -104.9903], endCoords: [40.7608, -111.8910] },
  { id: "PL-2045", type: "Petroleum Equipment", dims: "90'×14'×12'", weight: "55,000 lbs", from: "Houston, TX", to: "Baton Rouge, LA", date: "06/16/2026", escorts: 2, pay: "$1,200", status: "Open", company: "GulfStream Logistics", startCoords: [29.7604, -95.3698], endCoords: [30.4515, -91.1871] },
];

export default function LoadBoardPage({ isDarkMode, toggleTheme }) {
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [filter, setFilter] = useState("All");
  const [saved, setSaved] = useState([]);
  const [showConsent, setShowConsent] = useState(null);

  const statuses = ["All", "Open", "Urgent"];

  const toggleSave = (e, load) => {
    e.stopPropagation();
    setSaved((s) => s.includes(load.id) ? s.filter((x) => x !== load.id) : [...s, load.id]);
  };

  return (
    <>
      {/* ───────────────────────────────────────────────────────────────── */}
      {/* NAVBAR */}
      {/* ───────────────────────────────────────────────────────────────── */}
      <Navbar isDarkMode={isDarkMode} toggleTheme={toggleTheme} />

      {/* Added top padding (pt-24) to account for fixed navbar height */}
      <div className="max-w-7xl mx-auto px-4 py-6 md:px-8 md:py-8 pt-24 sm:pt-28 min-h-screen">
        
        {/* ───────────────────────────────────────────────────────────────── */}
        {/* CONSENT MODAL */}
        {/* ───────────────────────────────────────────────────────────────── */}
        {showConsent && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => setShowConsent(null)}
          >
            <div 
              className="w-full max-w-lg bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-800 flex flex-col max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between bg-slate-900 px-5 py-4 sticky top-0 z-10">
                <span className="font-bold text-white">Apply for {showConsent.id}</span>
                <button 
                  onClick={() => setShowConsent(null)}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Job Consent Agreement</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-6 leading-relaxed">
                  You are applying to escort a <strong>{showConsent.type}</strong> from <strong>{showConsent.from}</strong> to <strong>{showConsent.to}</strong>. 
                  By proceeding, you verify that your certifications cover these states and your equipment meets the required specs.
                </p>
                <div className="flex justify-end gap-3 mt-4">
                  <button 
                    onClick={() => setShowConsent(null)}
                    className="px-4 py-2 rounded-lg font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 dark:text-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={() => setShowConsent(null)}
                    className="px-4 py-2 rounded-lg font-bold text-white bg-cyan-600 hover:bg-cyan-700 transition-colors"
                  >
                    Sign & Submit
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ───────────────────────────────────────────────────────────────── */}
        {/* HEADER SECTION */}
        {/* ───────────────────────────────────────────────────────────────── */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight flex items-center gap-3">
              Load Board
              {saved.length > 0 && (
                <span className="text-sm font-bold text-red-500 bg-red-50 dark:bg-red-500/10 px-3 py-1 rounded-full flex items-center gap-1">
                  <Heart className="h-4 w-4 fill-current" /> {saved.length} saved
                </span>
              )}
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {LOAD_BOARD.length} open loads · Updated live
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl">
              {statuses.map((s) => (
                <button
                  key={s}
                  onClick={() => setFilter(s)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                    filter === s
                      ? "bg-white text-cyan-600 shadow-sm dark:bg-slate-700 dark:text-cyan-400"
                      : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
            <button className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-xl font-bold shadow-sm transition-all active:scale-95 text-sm">
              <Plus className="h-4 w-4" /> Post Load
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* ───────────────────────────────────────────────────────────────── */}
          {/* LOAD LIST */}
          {/* ───────────────────────────────────────────────────────────────── */}
          <div className={`flex flex-col gap-4 ${selectedLoad ? "lg:w-3/5 xl:w-2/3" : "w-full"}`}>
            {LOAD_BOARD.filter((l) => filter === "All" || l.status === filter).map((load) => {
              const isSelected = selectedLoad?.id === load.id;
              const isSaved = saved.includes(load.id);
              const isUrgent = load.status === "Urgent";

              return (
                <div
                  key={load.id}
                  onClick={() => setSelectedLoad(load)}
                  className={`group cursor-pointer rounded-2xl border p-5 transition-all ${
                    isSelected
                      ? "bg-cyan-50 border-cyan-500 ring-1 ring-cyan-500 shadow-md dark:bg-cyan-900/10 dark:border-cyan-400 dark:ring-cyan-400"
                      : "bg-white border-slate-200 hover:shadow-md hover:border-slate-300 dark:bg-slate-900 dark:border-slate-800 dark:hover:border-slate-700"
                  }`}
                >
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <div className="flex items-center gap-3 mb-1.5">
                        <span className="font-extrabold text-slate-900 dark:text-white tracking-tight">
                          {load.id}
                        </span>
                        <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full border uppercase tracking-wider ${
                          isUrgent 
                            ? "bg-red-50 text-red-600 border-red-200 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400" 
                            : "bg-green-50 text-green-600 border-green-200 dark:bg-green-500/10 dark:border-green-500/20 dark:text-green-400"
                        }`}>
                          {load.status}
                        </span>
                      </div>
                      <h3 className="text-base font-bold text-slate-800 dark:text-slate-200 mb-0.5">
                        {load.type}
                      </h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {load.company}
                      </p>
                    </div>

                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <div className="text-xl md:text-2xl font-black text-amber-500">
                        {load.pay}
                      </div>
                      <span className="text-xs text-slate-400 font-medium uppercase tracking-wide">
                        per escort
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-x-6 gap-y-3 mb-5 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700/50">
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300">
                      <MapPin className="h-3.5 w-3.5 text-cyan-500" /> {load.from} → {load.to}
                    </span>
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300">
                      <Calendar className="h-3.5 w-3.5 text-cyan-500" /> {load.date}
                    </span>
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300">
                      <Users className="h-3.5 w-3.5 text-cyan-500" /> {load.escorts} escort{load.escorts > 1 ? "s" : ""}
                    </span>
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300">
                      <Scale className="h-3.5 w-3.5 text-cyan-500" /> {load.weight}
                    </span>
                  </div>

                  <div className="flex items-center gap-3 hidden lg:flex">
                    <button 
                      onClick={(e) => { e.stopPropagation(); setShowConsent(load); }}
                      className="flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded-lg font-bold text-sm transition-colors shadow-sm dark:bg-cyan-500 dark:text-slate-900 dark:hover:bg-cyan-400"
                    >
                      <PenTool className="h-4 w-4" /> Apply Now
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setSelectedLoad(load); }}
                      className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-lg font-bold text-sm transition-colors dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                    >
                      View Details
                    </button>
                    <button 
                      onClick={(e) => toggleSave(e, load)}
                      className="ml-auto p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                      <Heart className={`h-5 w-5 transition-colors ${isSaved ? "fill-red-500 text-red-500" : "text-slate-400"}`} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ───────────────────────────────────────────────────────────────── */}
          {/* DESKTOP DETAILS PANEL */}
          {/* ───────────────────────────────────────────────────────────────── */}
          {selectedLoad && (
            <div className="hidden lg:block lg:w-2/5 xl:w-1/3 flex-shrink-0 animate-in slide-in-from-right-4 duration-300">
              <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xl p-6 sticky top-24">
                
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight mb-1">
                      {selectedLoad.id}
                    </h2>
                    <p className="text-base font-semibold text-slate-500 dark:text-slate-400">
                      {selectedLoad.company}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={(e) => toggleSave(e, selectedLoad)}
                      className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                      <Heart className={`h-6 w-6 transition-colors ${saved.includes(selectedLoad.id) ? "fill-red-500 text-red-500" : "text-slate-400"}`} />
                    </button>
                    <button 
                      onClick={() => setSelectedLoad(null)}
                      className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 dark:hover:text-slate-200 transition-colors"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                {/* VISUAL ROUTE MAP EMBED */}
                <div className="w-full h-48 bg-slate-200 dark:bg-slate-950 rounded-xl mb-6 overflow-hidden relative border border-slate-200 dark:border-slate-700 pointer-events-none">
                  <RouteMap 
                    startCoords={selectedLoad.startCoords} 
                    endCoords={selectedLoad.endCoords} 
                    startLabel={selectedLoad.from} 
                    endLabel={selectedLoad.to} 
                    isDarkMode={isDarkMode}
                  />
                </div>

                <div className="space-y-4">
                  {[
                    ["Load Type", selectedLoad.type],
                    ["Dimensions", selectedLoad.dims],
                    ["Weight", selectedLoad.weight],
                    ["Pickup", selectedLoad.from],
                    ["Delivery", selectedLoad.to],
                    ["Date", selectedLoad.date],
                    ["Escorts Needed", `${selectedLoad.escorts} certified pilot car(s)`],
                    ["Pay Rate", selectedLoad.pay + " per escort"],
                  ].map(([label, value], i) => (
                    <div key={i} className="flex justify-between items-center py-3 border-b border-slate-100 dark:border-slate-800/80 last:border-0">
                      <span className="text-sm font-medium text-slate-500 dark:text-slate-400">{label}</span>
                      <span className="text-sm font-bold text-slate-900 dark:text-slate-200 text-right">{value}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-8 flex flex-col gap-3">
                  <button 
                    onClick={() => setShowConsent(selectedLoad)}
                    className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-4 py-3.5 rounded-xl font-bold transition-all shadow-sm active:scale-95"
                  >
                    <PenTool className="h-5 w-5" /> Apply for This Load
                  </button>
                  <button 
                    className="w-full flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-3.5 rounded-xl font-bold transition-all dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                  >
                    <MessageSquare className="h-5 w-5" /> Contact Company
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}